import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBackTarget } from '../../hooks/useBackTarget';
import { clearVenueSetupReturnPath, getVenueSetupReturnPath } from '../../utils/venueSetupBackNav';

// The venue-setup checklist and the venue-settings shell share the same
// sub-pages (Photos, Vibe, Hours, Socials). Owners still finishing setup
// should always be returned to `/venue/setup-mode` — even if React Router
// state and our sessionStorage stash both got dropped (Capacitor
// cold-launch, hard reload, history-replace redirect chain).
const readUserVenueStatus = () => {
  try {
    const raw = window.localStorage?.getItem('user');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return parsed?.venue_status || '';
  } catch {
    return '';
  }
};

/**
 * Styled 38×38 back button used on every venue-setup sub-page (Photos,
 * Vibe, Hours, Socials). Resolution order:
 *   1. explicit `to` prop (page opts out of dynamic resolution)
 *   2. `location.state?.from` (React Router state — normal SPA nav)
 *   3. `sessionStorage.venue_setup_return_to` (survives cold-starts /
 *      state-loss on Capacitor)
 *   4. `useBackTarget` fallback (routeParents.js map)
 *
 * Matches the visual language of `VenueSetupModeHome`'s main back
 * button (surface bg, card border, rounded 12).
 */
export function VenueSetupBackButton({ to, testId = 'venue-setup-back-button', onBeforeNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const fallbackGoBack = useBackTarget();

  const handleClick = async () => {
    // Give the page a chance to flush any pending debounced save first,
    // otherwise edits made in the last ~500ms are silently dropped.
    if (typeof onBeforeNavigate === 'function') {
      try { await onBeforeNavigate(); } catch { /* swallow — never block back nav on a save failure */ }
    }
    if (to) {
      navigate(to);
      clearVenueSetupReturnPath();
      return;
    }
    // Owners still finishing setup should ALWAYS land back on the
    // checklist. This wins over `state.from` / sessionStorage so a
    // stale stash (e.g. after switching venues, or entering via a
    // deep link into the sub-page) can't ever misroute them.
    if (readUserVenueStatus() === 'setup_mode') {
      navigate('/venue/setup-mode');
      clearVenueSetupReturnPath();
      return;
    }
    const stateFrom = location.state?.from;
    if (stateFrom) {
      navigate(stateFrom);
      clearVenueSetupReturnPath();
      return;
    }
    const stashed = getVenueSetupReturnPath();
    if (stashed) {
      navigate(stashed);
      clearVenueSetupReturnPath();
      return;
    }
    fallbackGoBack();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={testId}
      aria-label="Back"
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--card-border)',
        borderRadius: 12,
        width: 38,
        height: 38,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: 'var(--premium-shadow-nested)',
      }}
    >
      <ChevronLeft size={20} color="var(--text)" strokeWidth={2.2} />
    </button>
  );
}

export default VenueSetupBackButton;
